import React, { useState, useEffect } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { all_routes } from "../../../Router/all_routes";
import { Link } from "react-router-dom";
import Table from "../../../core/pagination/datatable";
import TableTop from "../../../Components/UI/TableTop";
import Loader from "../../../feature-module/loader/loader";
import { useTableSearch } from "../../../core/Operations/useTableSearch";
import { Edit, PlusCircle } from "feather-icons-react/build/IconComponents";
import Error404 from "../../ErrorPages/error404";
import { useGetAllDepartmentQuery } from "../../../AppQuries/master/Department/get-all-departments";
import DepartmentFrom from "./DepartmentFrom";
import { useForm, FormProvider } from "react-hook-form";
import { loggedInUser } from "../../../core/redux/slice/authSlice";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { useAddDepartmentMutation } from "../../../AppQuries/master/Department/add-department";
import { useUpdateDepartmentMutation } from "../../../AppQuries/master/Department/update-department";
import ToastNotification from "../../../Components/UI/ToastNotification";
import { yupResolver } from "@hookform/resolvers/yup";
import { departmentValidationSchema } from "../../../core/Operations/ValidationSchema/MasterValidationSchema";
import LoadingSpinner from "Components/UI/LoadingSpinner";
import { toast } from "core/Operations/toast";
import { BackIcon } from "Components/UI/Icon/Icons";
import ReportTools from "Components/UI/ReportTools";
import { Switch } from "antd";
import Can from "core/Operations/Can";
import { accessCodes } from "core/json/accessCode";
import { Trash2 } from "react-feather";
import showConfirmationAlert from "Components/UI/Confirmation";
import { useDeleteDepartmentMutation } from "AppQuries/master/Department/delete-department";

const DepartmentList = () => {
  const userData = useSelector(loggedInUser);
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const methods = useForm({
    defaultValues: {
      code: 0,
      isActive: 1,
      companyCode: userData.companyCode,
      createUser: userData.userName,
      modifyUser: userData.userName,
      createDate: `${format(new Date(), "yyyy-MM-dd")}T${format(
        new Date(),
        "HH:mm:ss.SSS"
      )}Z`,
      modifyDate: `${format(new Date(), "yyyy-MM-dd")}T${format(
        new Date(),
        "HH:mm:ss.SSS"
      )}Z`,
    },
    resolver: yupResolver(departmentValidationSchema),
  });
  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch: GetDepartments,
  } = useGetAllDepartmentQuery({
    companyCode: userData?.companyCode,
    isTrue: showActiveOnly,
  });
  const { mutate: addDepartment } = useAddDepartmentMutation();
  const { mutate: updateDepartment } = useUpdateDepartmentMutation();
  const { mutate: deleteDepartment } = useDeleteDepartmentMutation();

  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [isMount, setIsMount] = useState(false);

  const toggleFilterVisibility = () => {
    setIsFilterVisible((prevVisibility) => !prevVisibility);
  };

  const [show, setShow] = useState(false);
  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const departmentData = data?.data.map((d, _id) => ({
    id: _id + 1,
    ...d,
  }));

  const { setSearchValue, filteredData } = useTableSearch(departmentData, [
    "description",
    "isActive",
  ]);

  const handleAddClick = () => {
    setSelectedDepartment(null);
    methods.reset({
      companyCode: userData.companyCode,
      description: "",
      isActive: 1,
      createUser: userData.userName,
      createDate: `${format(new Date(), "yyyy-MM-dd")}T${format(
        new Date(),
        "HH:mm:ss.SSS"
      )}Z`,
      modifyUser: userData.userName,
      modifyDate: `${format(new Date(), "yyyy-MM-dd")}T${format(
        new Date(),
        "HH:mm:ss.SSS"
      )}Z`,
    });
    setShow(true);
  };

  const handleEditClick = (department) => {
    setSelectedDepartment(department);
    methods.reset({
      companyCode: department.companyCode,
      description: department.description,
      isActive: department.isActive ? 1 : 0,
      createUser: department.createUser,
      createDate: department.createDate,
      modifyUser: userData.userName,
      modifyDate: `${format(new Date(), "yyyy-MM-dd")}T${format(
        new Date(),
        "HH:mm:ss.SSS"
      )}Z`,
    });
    setShow(true);
  };

  const confirmDelete = async (department) => {
    const result = await showConfirmationAlert({
      title: "Are you sure?",
      text: `Do you want to delete the department "${department.description}"?`,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    });
    if (result.isConfirmed) {
      const payload = {
        companyCode: department.companyCode,
        code: department.code,
      };
      deleteDepartment(payload, {
        onSuccess: (data) => {
          if (data) {
            toast.success("Department Deleted Successfully.");
            GetDepartments();
          } else {
            toast.warning("Something went wrong.");
          }
        },
        onError: (error) => {
          toast.error(error.message || "Something went wrong.");
        },
      });
    }
  };

  const onSubmit = async (payload) => {
    if (selectedDepartment) {
      payload.code = selectedDepartment.code;
      updateDepartment(payload, {
        onSuccess: (data) => {
          if (data?.data) {
            toast.success("Department Updated Successfully.");
            setSelectedDepartment(null);
            setShow(false);
            GetDepartments();
          } else {
            toast.warning("Something went wrong.");
          }
        },
        onError: (error) => {
          toast.error(error.message || "Something went wrong.");
        },
      });
    } else {
      payload.code = 0;
      addDepartment(payload, {
        onSuccess: (data) => {
          if (data?.data) {
            toast.success("Department Added Successfully.");
            setShow(false);
            GetDepartments();
          } else {
            toast.warning(error.message || "Something went wrong.");
          }
        },
        onError: (error) => {
          toast.error(error.message || "Something went wrong.");
        },
      });
    }
  };

  useEffect(() => {
    if (isMount) GetDepartments();

    setIsMount(true);
  }, [showActiveOnly]);

  const columns = [
    {
      title: "SNo",
      dataIndex: "id",
    },
    {
      title: "Department Name",
      dataIndex: "description",
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (text, record) => (
        <>
          {text === true && (
            <div className="badges badge-linesuccess">Active</div>
          )}
          {text === false && (
            <div className="badges badge-linedanger">In Active</div>
          )}
        </>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      className: "action-table-data",
      render: (text, record) => (
        <div className="edit-delete-action">
          <div className="input-block add-lists"></div>
          <Can
            accessModule={accessCodes.human_resources}
            accessCode={accessCodes.departments}
            accessType="haveEdit"
          >
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="edit-tooltip">Edit</Tooltip>}
            >
              <Link
                className="me-2 p-2"
                to="#"
                onClick={() => handleEditClick(record)}
              >
                <Edit className="feather-edit" />
              </Link>
            </OverlayTrigger>
          </Can>

          <Can
            accessModule={accessCodes.human_resources}
            accessCode={accessCodes.departments}
            accessType="haveDelete"
          >
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id="edit-tooltip">Delete</Tooltip>}
            >
              <Link
                className="me-2 p-2"
                to="#"
                onClick={() => confirmDelete(record)}
              >
                <Trash2 className="feather-delete" />
              </Link>
            </OverlayTrigger>
          </Can>
        </div>
      ),
    },
  ];

  if (isLoading || isFetching) return <LoadingSpinner />;
  if (isError) return <Error404 error={error} />;

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Departments</h4>
                <h6>Manage your departments</h6>
              </div>
            </div>
            <div className="page-btn">
              <ReportTools
                columns={columns}
                data={filteredData}
                fileName="Department Master"
                isExcel={true}
                isPdf={true}
                pdfTheme="striped"
                refresh={GetDepartments}
              />
            </div>
            <Can
              accessModule={accessCodes.human_resources}
              accessCode={accessCodes.departments}
              accessType="haveAdd"
            >
              <div className="page-btn">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="add-new-tooltip">Add New Department</Tooltip>
                  }
                >
                  <Link
                    to="#"
                    className="btn btn-added"
                    onClick={handleAddClick}
                  >
                    <PlusCircle className="me-2" />
                    Add New Department
                  </Link>
                </OverlayTrigger>
              </div>
            </Can>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="card table-list-card border-0">
                <div className="card-body">
                  <TableTop
                    onSearch={setSearchValue}
                    toggleFilterVisibility={toggleFilterVisibility}
                    isFilterVisible={isFilterVisible}
                    showToggle={true}
                  />
                  <div
                    className={`card ${isFilterVisible ? " visible" : ""}`}
                    id="filter_inputs"
                    // style={{ display: isFilterVisible ? "block" : "none" }}
                  >
                    <div className="card-body pb-0">
                      <div className="form-row row">
                        <div className="row">
                          <div className="col-lg-3 col-sm-6 col-12 mb-3">
                            <div className="form-group">
                              <Switch
                                checked={showActiveOnly}
                                checkedChildren="Show Active"
                                unCheckedChildren="Show All"
                                onChange={(checked) =>
                                  setShowActiveOnly(checked)
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <Table
                      className="table datanew"
                      columns={columns}
                      dataSource={filteredData}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FormProvider {...methods}>
        <DepartmentFrom
          mode={selectedDepartment ? "E" : "I"}
          onSubmit={onSubmit}
          show={show}
          handleClose={handleClose}
        />
      </FormProvider>
    </div>
  );
};

export default DepartmentList;

// import React, { useState, useEffect } from "react";
// import { OverlayTrigger, Tooltip } from "react-bootstrap";
// import { all_routes } from "../../../Router/all_routes";
// import { Link } from "react-router-dom";
// import Table from "../../../core/pagination/datatable";
// import TableTop from "../../../Components/UI/TableTop";
// import Loader from "../../../feature-module/loader/loader";
// import { useTableSearch } from "../../../core/Operations/useTableSearch";
// import {
//   Edit,
//   PlusCircle,
//   RotateCcw,
//   Trash2,
// } from "feather-icons-react/build/IconComponents";
// import { Eye } from "react-feather";
// import Error404 from "../../ErrorPages/error404";
// import { useGetAllDepartmentQuery } from "../../../AppQuries/master/Department/get-all-departments";
// import DepartmentFrom from "./DepartmentFrom";
// import { useForm, FormProvider } from "react-hook-form";
// import { loggedInUser } from "../../../core/redux/slice/authSlice";
// import { useSelector } from "react-redux";
// import { format } from "date-fns";
// import { useAddDepartmentMutation } from "../../../AppQuries/master/Department/add-department";
// import ToastNotification from "../../../Components/UI/ToastNotification";
// import { yupResolver } from "@hookform/resolvers/yup";
// import {departmentValidationSchema} from "../../../core/Operations/ValidationSchema/MasterValidationSchema";
// const DepartmentList = () => {
//   const userData = useSelector(loggedInUser);
//   const route = all_routes;
//   const methods = useForm({
//     defaultValues: {
//       isActive: 1,
//       companyCode: userData.companyCode,
//       userName: userData.userName,
//       currentDate: `${format(new Date(), "yyyy-MM-dd")}T${format(
//         new Date(),
//         "HH:mm:ss.SSS"
//       )}Z`,
//     },
//     resolver: yupResolver(departmentValidationSchema),
//   });
//   const { data, isLoading, isError, error, refetch : GetDepartments } = useGetAllDepartmentQuery();

//   const departmentData = data?.data.map((d, _id) => ({
//     id: _id + 1,
//     ...d,
//   }));

//   const [isFilterVisible, setIsFilterVisible] = useState(false);

//   const { mutate: addDepartment } = useAddDepartmentMutation();

//   const [message, setMessage] = useState("");
//   const [showErrorToast, setShowErrorToast] = useState(false);
//   const [showSuccessToast, setShowSuccessToast] = useState(false);

//   useEffect(() => {
//     const timeoutId = setTimeout(() => {
//       setShowErrorToast(false);
//       setShowSuccessToast(false);
//     }, 6000);
//     return () => clearTimeout(timeoutId);
//   }, [showErrorToast, showSuccessToast]);

//   const mode = "I";

//   const onSubmit = async (data) => {
//     if (mode === "I") {
//       var insertData = {
//         companyCode: data.companyCode,
//         code: 0,
//         description: data.description,
//         isActive: data.isActive,
//         createUser: data.userName,
//         createDate: data.currentDate,
//         modifyUser: data.userName,
//         modifyDate: data.currentDate,
//       };

//       addDepartment(insertData, {
//         onSuccess: (data) => {
//           console.log(data?.data);
//           console.log(insertData);
//           if (data?.data === false) {
//             setMessage(data?.data[0]?.errorMessage);
//             setShowErrorToast(true);
//           } else {
//             setMessage("Department Added Successfully.");
//             setShowSuccessToast(true);
//           }
//         },
//         onError: (error) => {
//           setMessage(error.message || "Something went wrong.");
//           setShowErrorToast(true);
//         },
//       });
//     }

//     GetDepartments();
//   };

//   const toggleFilterVisibility = () => {
//     setIsFilterVisible((prevVisibility) => !prevVisibility);
//   };

//   const addNewTooltip = (props) => (
//     <Tooltip id="add-new-tooltip" {...props}>
//       Add New Department
//     </Tooltip>
//   );

//   const columns = [
//     {
//       title: "SNo",
//       dataIndex: "id",
//     },
//     {
//       title: "Department Name",
//       dataIndex: "description",
//       sorter: (a, b) => a.description.length - b.description.length,
//     },

//     {
//       title: "Status",
//       dataIndex: "isActive",
//       render: (text, record) => (
//         <>
//           {record.isActive === true && (
//             <span className="badges badge-linesuccess">Active</span>
//           )}
//           {record.isActive === false && (
//             <span className="badges badge-linedanger">In Active</span>
//           )}
//         </>
//       ),
//     },

//     {
//       title: "Action",
//       dataIndex: "action",
//       render: (text, record) => (
//         <td className="action-table-data">
//           <div className="edit-delete-action">
//             <div className="input-block add-lists"></div>
//             <OverlayTrigger
//               placement="top"
//               overlay={<Tooltip id="edit-tooltip">Edit</Tooltip>}
//             >
//               <Link
//                 className="me-2 p-2"
//                 to="#"
//                 data-bs-toggle="modal"
//                 data-bs-target="#department-from"
//               >
//                 <Edit className="feather-edit" />
//               </Link>
//             </OverlayTrigger>
//             {/*
//             <Link
//               className="confirm-text p-2"
//               to="#"
//               onClick={showConfirmationAlert}
//             >
//               <Trash2 className="feather-trash-2" />
//             </Link> */}
//           </div>
//         </td>
//       ),
//       sorter: (a, b) => a.createdby.length - b.createdby.length,
//     },
//   ];

//   const searchColumns = columns.map((column) => column.dataIndex);
//   const { setSearchValue, filteredData } = useTableSearch(
//     departmentData,
//     searchColumns
//   );

//   if (isLoading) {
//     return <Loader />;
//   }

//   if (isError) {
//     return <Error404 error={error} />;
//   }

//   return (
//     <div>
//       <div className="toast-container position-fixed top-0 end-0 p-3">
//         <ToastNotification
//           showSuccessToast={showSuccessToast}
//           showErrorToast={showErrorToast}
//           message={message}
//         />
//       </div>
//       <div className="page-wrapper">
//         <div className="content">
//           <div className="page-header">
//             <div className="add-item d-flex">
//               <div className="page-title">
//                 <h4>Dempartments</h4>
//                 <h6>Manage your dempartments</h6>
//               </div>
//             </div>
//             <div className="page-btn">
//               <OverlayTrigger placement="top" overlay={addNewTooltip}>
//                 <Link
//                   to="#"
//                   className="btn btn-added"
//                   data-bs-toggle="modal"
//                   data-bs-target="#department-from"
//                 >
//                   <PlusCircle className="me-2" />
//                   Add New Department
//                 </Link>
//               </OverlayTrigger>
//             </div>
//           </div>
//           <div className="row">
//             <div className="col-md-12">
//               <div className="card table-list-card border-0">
//                 <div className="card-body">
//                   <TableTop
//                     onSearch={setSearchValue}
//                     toggleFilterVisibility={toggleFilterVisibility}
//                     isFilterVisible={isFilterVisible}
//                   />

//                   <div className="table-responsive">
//                     <Table
//                       className="table  datanew"
//                       columns={columns}
//                       dataSource={filteredData}
//                     />
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       <FormProvider {...methods}>
//         <DepartmentFrom mode="I" onSubmit={onSubmit} />
//       </FormProvider>
//     </div>
//   );
// };

// export default DepartmentList;
